import type { CSSProperties, RefObject } from "react";

import type { CdropdownOption } from "./Cdropdown.types";
import { useDropdownActiveOptionScroll } from "./useDropdownActiveOptionScroll";
import { useDropdownOptionElementRefs } from "./useDropdownOptionElementRefs";
import { useDropdownOptionsHeightMeasurement } from "./useDropdownOptionsHeightMeasurement";
import { useDropdownOptionsViewportStyles } from "./useDropdownOptionsViewportStyles";

interface UseDropdownOptionsViewportInput {
  isOpen: boolean;
  options: CdropdownOption[];
  effectiveActiveIndex: number | null;
  maxHeight: number | undefined;
  minHeight: number | undefined;
  optionsLength: number;
}

interface UseDropdownOptionsViewportResult {
  optionsContainerRef: RefObject<HTMLDivElement | null>;
  optionsContentRef: RefObject<HTMLDivElement | null>;
  optionsViewportHeight: number | null;
  optionsViewportStyle: CSSProperties;
  optionsWrapperStyle: CSSProperties;
  onOptionElementRef: (input: {
    index: number;
    element: HTMLDivElement | null;
  }) => void;
}

export function useDropdownOptionsViewport({
  isOpen,
  options,
  effectiveActiveIndex,
  maxHeight,
  minHeight,
  optionsLength,
}: UseDropdownOptionsViewportInput): UseDropdownOptionsViewportResult {
  const { optionsContainerRef, optionItemRefs, onOptionElementRef } =
    useDropdownOptionElementRefs({ optionsLength });
  const { optionsContentRef, optionsViewportHeight } =
    useDropdownOptionsHeightMeasurement({
      isOpen,
      options,
      maxHeight,
      minHeight,
    });
  useDropdownActiveOptionScroll({
    isOpen,
    effectiveActiveIndex,
    optionsLength,
    optionsContainerRef,
    optionItemRefs,
  });
  const { optionsViewportStyle, optionsWrapperStyle } =
    useDropdownOptionsViewportStyles({
      optionsViewportHeight,
      maxHeight,
      minHeight,
    });

  return {
    optionsContainerRef,
    optionsContentRef,
    optionsViewportHeight,
    optionsViewportStyle,
    optionsWrapperStyle,
    onOptionElementRef,
  };
}
